import { useDroppable, useDraggable } from '@dnd-kit/core';
import { format, isSameDay, parseISO } from 'date-fns';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { X, Undo2 } from 'lucide-react';
import { assignmentAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function EmployeeCalendarRow({ employee, weekDays, assignments, workOrders, onRefresh, canEdit = true, viewMode = 'weekday', availability = {}, isAdmin = false }) {
  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getAssignmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return assignments.filter((a) => a.date === dateStr);
  };

  const getCapacityForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dateAssignments = getAssignmentsForDate(date);
    const used = dateAssignments.reduce((sum, a) => sum + a.hours_allocated, 0);
    
    // Check for availability override, otherwise use employee's default daily hours
    // Use nullish coalescing (??) instead of || to handle 0 correctly
    const hasOverride = availability.hasOwnProperty(dateStr);
    const defaultHours = employee.daily_hours !== undefined && employee.daily_hours !== null 
      ? employee.daily_hours 
      : 8;
    const totalHours = hasOverride ? availability[dateStr] : defaultHours;
    
    // Calculate percentage (avoid division by zero)
    const percentage = totalHours > 0 ? (used / totalHours) * 100 : (used > 0 ? 100 : 0);
    
    return { 
      used, 
      total: totalHours, 
      percentage,
      isUnavailable: totalHours === 0,
      hasOverride
    };
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await assignmentAPI.delete(assignmentId);
      toast.success('Assignment removed');
      onRefresh();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  return (
    <div className="grid border-b border-border hover:bg-accent/5 transition-colors" style={{ gridTemplateColumns: `200px repeat(${weekDays.length}, 1fr)` }}>
      <div className="p-4 border-r border-border flex items-center gap-3 bg-slate-50/50 dark:bg-slate-800/50">
        <Avatar>
          <AvatarFallback className="bg-sky-100 text-sky-700 font-bold">
            {getInitials(employee.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{employee.name}</p>
          <p className="text-xs text-muted-foreground truncate">{employee.email}</p>
        </div>
      </div>

      {weekDays.map((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayAssignments = getAssignmentsForDate(date);
        const capacity = getCapacityForDate(date);

        return (
          <DropZone
            key={dateStr}
            id={`${employee.id}_${dateStr}`}
            capacity={capacity}
            assignments={dayAssignments}
            workOrders={workOrders}
            onRemove={handleRemoveAssignment}
            onUnassign={handleUnassignWorkOrder}
            canEdit={canEdit}
            viewMode={viewMode}
            isAdmin={isAdmin}
          />
        );
      })}
    </div>
  );

  async function handleUnassignWorkOrder(workOrderId) {
    try {
      await assignmentAPI.unassignWorkOrder(workOrderId);
      toast.success('Work order returned to unassigned queue');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unassign work order');
    }
  }
}

function DropZone({ id, capacity, assignments, workOrders, onRemove, onUnassign, canEdit = true, viewMode = 'weekday', isAdmin = false }) {
  // Disable dropping if employee is unavailable (0 hours) or if user can't edit
  const isUnavailable = capacity.isUnavailable;
  const { setNodeRef, isOver } = useDroppable({ id, disabled: !canEdit || isUnavailable });
  const isCompactView = viewMode === 'rolling4' || viewMode === 'monthly';

  return (
    <div
      ref={setNodeRef}
      className={`drop-zone ${isCompactView ? 'p-1' : 'p-2'} border-r border-border ${
        isCompactView ? 'min-h-[80px]' : 'min-h-[100px]'
      } transition-colors ${
        isOver ? 'bg-accent/10 border-accent' : ''
      } ${isUnavailable ? 'bg-slate-100 dark:bg-slate-900/50' : ''}`}
      data-testid="drop-zone"
    >
      <div className={isCompactView ? 'space-y-1' : 'space-y-2'}>
        {assignments.map((assignment) => {
          const wo = workOrders.find((w) => w.id === assignment.work_order_id);
          if (!wo) return null;

          return (
            <DraggableAssignment
              key={assignment.id}
              assignment={assignment}
              workOrder={wo}
              onRemove={onRemove}
              onUnassign={onUnassign}
              canEdit={canEdit}
              isCompactView={isCompactView}
              isAdmin={isAdmin}
            />
          );
        })}
      </div>

      {!isCompactView && (
        <div className="mt-2">
          {isUnavailable ? (
            <div className="text-center">
              <span className="text-[10px] font-medium text-slate-500 bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded">
                Unavailable
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-[10px] font-mono mb-1">
                <span className="text-muted-foreground">
                  {capacity.used.toFixed(1)}/{capacity.total}h
                </span>
                <span
                  className={`font-semibold ${
                    capacity.percentage > 100
                      ? 'text-red-600'
                      : capacity.percentage > 80
                      ? 'text-amber-600'
                      : 'text-emerald-600'
                  }`}
                >
                  {capacity.percentage.toFixed(0)}%
                </span>
              </div>
              <Progress
                value={capacity.percentage}
                className={`h-1.5 capacity-bar ${
                  capacity.percentage > 100
                    ? '[&>*]:bg-red-500'
                    : capacity.percentage > 80
                    ? '[&>*]:bg-amber-500'
                    : '[&>*]:bg-emerald-500'
                }`}
              />
            </>
          )}
        </div>
      )}
      {isCompactView && capacity.percentage > 0 && (
        <div className="mt-1">
          <Progress
            value={capacity.percentage}
            className={`h-1 capacity-bar ${
              capacity.percentage > 100
                ? '[&>*]:bg-red-500'
                : capacity.percentage > 80
                ? '[&>*]:bg-amber-500'
                : '[&>*]:bg-emerald-500'
            }`}
          />
        </div>
      )}
    </div>
  );
}

// Draggable component for assigned work orders
function DraggableAssignment({ assignment, workOrder, onRemove, onUnassign, canEdit, isCompactView, isAdmin = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `assignment_${assignment.id}`,
    data: {
      type: 'assignment',
      assignment,
      workOrder,
    },
    disabled: !canEdit,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 1000,
      }
    : undefined;

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onRemove(assignment.id);
  };

  const handleUnassignClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof window === 'undefined' || window.confirm(`Unassign ${workOrder.wo_number} and return it to the unassigned queue?`)) {
      onUnassign(workOrder.id);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`bg-white dark:bg-slate-800 border border-border rounded ${
        isCompactView ? 'p-1' : 'p-2'
      } ${isCompactView ? 'text-[10px]' : 'text-xs'} group relative ${
        isDragging ? 'opacity-50 shadow-lg ring-2 ring-sky-500' : ''
      } ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''}`}
      data-testid="assigned-work-order"
    >
      {canEdit && !isCompactView && (
        <>
          <button
            onClick={handleRemoveClick}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            data-testid="remove-assignment-button"
            title="Remove this assignment"
          >
            <X className="h-3 w-3" />
          </button>
          {isAdmin && (
            <button
              onClick={handleUnassignClick}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute -top-1 -left-1 h-4 w-4 bg-amber-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
              data-testid="unassign-button"
              title="Return to unassigned queue"
            >
              <Undo2 className="h-2.5 w-2.5" />
            </button>
          )}
        </>
      )}
      <div className="font-bold font-mono truncate">{workOrder.wo_number}</div>
      {!isCompactView && (
        <>
          {/* Part Number (Assembly Item) */}
          {workOrder.assembly_item && (
            <div className="text-[10px] font-medium text-sky-600 truncate">
              {workOrder.assembly_item}
            </div>
          )}
          {/* Customer */}
          <div className="text-muted-foreground truncate text-[10px]">
            {workOrder.customer}
          </div>
          {/* Additional fields: Tag and SN */}
          <div className="text-[9px] text-muted-foreground mt-0.5 space-y-0">
            {workOrder.tag && (
              <div className="truncate"><span className="font-semibold">Tag:</span> {workOrder.tag}</div>
            )}
            {workOrder.serial_number && (
              <div className="truncate"><span className="font-semibold">SN:</span> {workOrder.serial_number}</div>
            )}
          </div>
        </>
      )}
      <Badge 
        className={`${isCompactView ? 'mt-0.5 text-[9px]' : 'mt-1 text-[10px]'} font-mono font-semibold ${
          workOrder.buildable <= 0 
            ? 'bg-red-500 text-white hover:bg-red-600' 
            : ''
        }`} 
        variant={workOrder.buildable <= 0 ? 'default' : 'secondary'}
      >
        {assignment.hours_allocated}h
      </Badge>
    </div>
  );
}
